"use client";

import { useState } from "react";
import {
  ChartStyleFields,
  useChartStyle,
} from "@/components/tool-kit/charts/controls";
import { ChartFigure, toTsv } from "@/components/tool-kit/charts/figure";
import {
  Checkbox,
  Notice,
  PillGroup,
  SelectField,
  TextAreaField,
  TextField,
} from "@/components/tool-kit/primitives";
import { BUTTON_SECONDARY, HINT } from "@/components/tool-kit/styles";
import { ForestDiagram } from "@/components/tools/diagrams-more/forest-diagram";
import {
  useToolLocale,
  useToolNumber,
  useToolUi,
} from "@/components/tools/tool-ui";
import { t, tPlural } from "@/lib/i18n/interpolate";
import { colorIndex, paletteColor } from "@/lib/tool-kit/charts/color";
import { usesDecimalComma } from "@/lib/tool-kit/charts/format";
import { chartFilename } from "@/lib/tool-kit/charts/layout";
import {
  type ForestIssue,
  type ForestScale,
  forestRows,
  layoutForest,
  parseForest,
} from "@/lib/tool-kit/diagrams-more/forest";

/** English strings. A translated content file overrides these through `ui`. */
export const FOREST_PLOT_MAKER_STRINGS = {
  inputLabel:
    "Your studies — one per line: Study, estimate, lower limit, upper limit, weight (optional)",
  inputHint:
    "Write Adams 2015, 0.72, 0.48, 1.08, 14.2 — the study's name, its estimate, the lower and upper limits of its confidence interval, and optionally its weight in percent. A first line of column names is ignored. Use semicolons between cells if your numbers use decimal commas.",
  exampleText:
    "Study, OR, Lower, Upper, Weight %\nAdams 2015, 0.72, 0.48, 1.08, 14.2\nBaker 2016, 0.65, 0.41, 1.03, 11.8\nChen 2018, 0.88, 0.70, 1.11, 27.5\nDiaz 2019, 0.59, 0.37, 0.94, 10.4\nEvans 2021, 0.81, 0.66, 0.99, 36.1\nOverall, 0.78, 0.68, 0.89",
  loadExample: "Load the example",
  noPooling:
    "The plot draws exactly the numbers you enter. It does not pool studies or re-estimate anything — enter a pooled result yourself for the summary row.",
  scaleLabel: "Type of effect measure",
  scaleLog: "Ratio (odds, risk or hazard ratio) — log scale, no effect at 1",
  scaleLinear:
    "Difference (mean difference, SMD, correlation) — linear scale, no effect at 0",
  summaryRow: "Draw the last row as a summary diamond",
  decimalsLabel: "Decimal places in the numbers",
  measureLabel: "Name of the effect measure",
  measureDefault: "Odds ratio",
  ciLabel: "Name of the interval",
  ciDefault: "95% CI",
  leftLabel: "Label at the left of the line of no effect (optional)",
  rightLabel: "Label at the right (optional)",
  leftPlaceholder: "e.g. Favours treatment",
  rightPlaceholder: "e.g. Favours control",
  exampleLeft: "Favours treatment",
  exampleRight: "Favours control",
  headerStudy: "Study",
  headerEstimate: "{measure} [{interval}]",
  headerWeight: "Weight",
  studiesDetected: "one:{count} study|other:{count} studies",
  enterData:
    "Enter at least one study with an estimate and its limits to draw the plot.",
  issues: {
    badRow:
      "“{text}” needs an estimate and its lower and upper limits, so it was left out.",
    order:
      "“{text}”: the estimate must lie between the limits (lower ≤ estimate ≤ upper), so it was left out.",
    positive:
      "“{text}”: ratios must be above 0 on a log scale, so it was left out. Choose the difference scale if values can be 0 or below.",
    weight: "“{text}”: the weight must be 0 or more, so it was left out.",
  },
  moreIssues: "one:…and {count} more notice.|other:…and {count} more notices.",
  studyColumn: "Study",
  estimateColumn: "Estimate",
  lowerColumn: "Lower limit",
  upperColumn: "Upper limit",
  weightColumn: "Weight (%)",
  summaryTag: "summary",
  labelsAndStyle: "Title and figure style",
  chartTitle: "Title (optional)",
  xAxisTitle: "Axis title",
  axisTitlePlaceholderX: "e.g. Odds ratio (log scale)",
  xTitleLog: "Odds ratio (log scale)",
  xTitleLinear: "Effect size (linear scale)",
  colorLabel: "Color of the study squares",
  colorBlue: "Blue",
  colorOrange: "Orange",
  colorGreen: "Bluish green",
  colorVermillion: "Vermillion",
  colorSkyBlue: "Sky blue",
  colorPurple: "Reddish purple",
  colorYellow: "Yellow",
  colorBlack: "Black",
  apaStyle: "APA style figure",
  apaHint:
    "Takes the title out of the image and sets the figure number in bold and the title in italics above it — the APA 7 layout.",
  figureNumber: "Figure number",
  figureNote: "Note (optional)",
  figureLabel: "Figure {number}",
  noteLabel: "Note.",
  copyCaption: "Copy number and title",
  downloadSvg: "Download SVG",
  downloadPng: "Download PNG",
  copyData: "Copy as table",
  copied: "Copied!",
  exportFailed:
    "This browser could not create the PNG. Try Download SVG, or another browser.",
  showDataTable: "Show the plot as a table",
  chartLabel: "Forest plot",
  chartDescription:
    "Forest plot of {studies} on a {scale} scale, with the line of no effect at {none}. {list}.",
  scaleWordLog: "logarithmic",
  scaleWordLinear: "linear",
  describeRow: "{study}: {estimate}, {interval} {lower} to {upper}",
} as const;

type Strings = typeof FOREST_PLOT_MAKER_STRINGS;

const SHOWN_ISSUES = 3;

function Controls({
  strings,
  scale,
  setScale,
  summary,
  setSummary,
  decimals,
  setDecimals,
  measure,
  setMeasure,
  interval,
  setInterval,
  left,
  setLeft,
  right,
  setRight,
}: {
  strings: Strings;
  scale: ForestScale;
  setScale: (scale: ForestScale) => void;
  summary: boolean;
  setSummary: (value: boolean) => void;
  decimals: string;
  setDecimals: (value: string) => void;
  measure: string;
  setMeasure: (value: string) => void;
  interval: string;
  setInterval: (value: string) => void;
  left: string;
  setLeft: (value: string) => void;
  right: string;
  setRight: (value: string) => void;
}) {
  return (
    <div className="space-y-5">
      <PillGroup
        label={strings.scaleLabel}
        name="fpm-scale"
        onChange={setScale}
        options={[
          { value: "log", label: strings.scaleLog },
          { value: "linear", label: strings.scaleLinear },
        ]}
        value={scale}
      />
      <div className="grid gap-4 sm:grid-cols-3">
        <TextField
          id="fpm-measure"
          label={strings.measureLabel}
          onChange={setMeasure}
          value={measure}
        />
        <TextField
          id="fpm-interval"
          label={strings.ciLabel}
          onChange={setInterval}
          value={interval}
        />
        <SelectField
          id="fpm-decimals"
          label={strings.decimalsLabel}
          onChange={setDecimals}
          options={["1", "2", "3"].map((value) => ({ value, label: value }))}
          value={decimals}
        />
      </div>
      <div className="grid gap-4 sm:grid-cols-2">
        <TextField
          id="fpm-left"
          label={strings.leftLabel}
          onChange={setLeft}
          placeholder={strings.leftPlaceholder}
          value={left}
        />
        <TextField
          id="fpm-right"
          label={strings.rightLabel}
          onChange={setRight}
          placeholder={strings.rightPlaceholder}
          value={right}
        />
      </div>
      <Checkbox
        checked={summary}
        label={strings.summaryRow}
        onChange={setSummary}
      />
    </div>
  );
}

function issueText(strings: Strings, issue: ForestIssue): string {
  return t(strings.issues[issue.kind], { text: issue.text });
}

export default function ForestPlotMakerWidget() {
  const strings = useToolUi(FOREST_PLOT_MAKER_STRINGS);
  const locale = useToolLocale();
  const fmt = useToolNumber();
  const [raw, setRaw] = useState<string>(strings.exampleText);
  const [scale, setScale] = useState<ForestScale>("log");
  const [summary, setSummary] = useState(true);
  const [decimals, setDecimals] = useState("2");
  const [measure, setMeasure] = useState<string>(strings.measureDefault);
  const [interval, setInterval] = useState<string>(strings.ciDefault);
  const [left, setLeft] = useState<string>(strings.exampleLeft);
  const [right, setRight] = useState<string>(strings.exampleRight);
  const [style, update] = useChartStyle({
    title: "",
    xTitle: strings.xTitleLog,
    yTitle: "",
    color: "blue",
    valueLabels: false,
    apa: false,
    figureNumber: "1",
    note: "",
  });

  function changeScale(next: ForestScale) {
    setScale(next);
    const from = next === "log" ? strings.xTitleLinear : strings.xTitleLog;
    if (style.xTitle === from) {
      update({
        xTitle: next === "log" ? strings.xTitleLog : strings.xTitleLinear,
      });
    }
  }

  const parse = parseForest(raw, scale, summary);
  const places = Number(decimals);
  const format = (value: number) =>
    fmt(value, {
      minimumFractionDigits: places,
      maximumFractionDigits: places,
    });
  const headers = {
    study: strings.headerStudy,
    estimate: t(strings.headerEstimate, { measure, interval }),
    weight: strings.headerWeight,
  };
  const layout =
    parse.rows.length > 0
      ? layoutForest(parse.rows, {
          scale,
          format,
          weightFormat: (value) =>
            fmt(value, { minimumFractionDigits: 1, maximumFractionDigits: 1 }),
          tick: (value) => fmt(value, { maximumSignificantDigits: 3 }),
          separator: usesDecimalComma(fmt) ? "; " : ", ",
          headers,
          leftLabel: left.trim(),
          rightLabel: right.trim(),
          xTitle: style.xTitle.trim(),
        })
      : null;
  const rows = forestRows(parse).map((row) => [
    row.summary ? `${row.label} (${strings.summaryTag})` : row.label,
    format(row.estimate),
    format(row.lower),
    format(row.upper),
    row.weight === null ? "" : format(row.weight),
  ]);
  const head = [
    strings.studyColumn,
    strings.estimateColumn,
    strings.lowerColumn,
    strings.upperColumn,
    strings.weightColumn,
  ];
  const shown = parse.issues.slice(0, SHOWN_ISSUES);
  const hidden = parse.issues.length - shown.length;
  const describe = parse.rows
    .map((row) =>
      t(strings.describeRow, {
        study: row.label,
        estimate: format(row.estimate),
        interval,
        lower: format(row.lower),
        upper: format(row.upper),
      })
    )
    .join("; ");

  return (
    <div className="space-y-6">
      <TextAreaField
        hint={strings.inputHint}
        id="fpm-input"
        label={strings.inputLabel}
        onChange={setRaw}
        rows={9}
        value={raw}
      />
      <div className="flex flex-wrap gap-3">
        <button
          className={BUTTON_SECONDARY}
          onClick={() => setRaw(strings.exampleText)}
          type="button"
        >
          {strings.loadExample}
        </button>
      </div>
      <p className={HINT}>{strings.noPooling}</p>
      <Controls
        decimals={decimals}
        interval={interval}
        left={left}
        measure={measure}
        right={right}
        scale={scale}
        setDecimals={setDecimals}
        setInterval={setInterval}
        setLeft={setLeft}
        setMeasure={setMeasure}
        setRight={setRight}
        setScale={changeScale}
        setSummary={setSummary}
        strings={strings}
        summary={summary}
      />
      <div aria-live="polite" className="space-y-2">
        {layout ? (
          <p className={HINT}>
            {tPlural(strings.studiesDetected, locale, parse.rows.length, {
              count: fmt(parse.rows.length),
            })}
          </p>
        ) : (
          <Notice>{strings.enterData}</Notice>
        )}
        {shown.map((issue) => (
          <Notice key={`${issue.kind}-${issue.text}`}>
            {issueText(strings, issue)}
          </Notice>
        ))}
        {hidden > 0 ? (
          <p className={HINT}>
            {tPlural(strings.moreIssues, locale, hidden, {
              count: fmt(hidden),
            })}
          </p>
        ) : null}
      </div>
      {layout ? (
        <ChartFigure
          apa={style.apa}
          data={toTsv([head, ...rows])}
          figureNumber={style.figureNumber}
          filename={chartFilename(style.title || measure, "forest-plot")}
          labels={strings}
          note={style.note}
          table={{ head, rows }}
          title={style.title}
        >
          <ForestDiagram
            color={paletteColor(colorIndex(style.color), 0)}
            description={t(strings.chartDescription, {
              studies: tPlural(
                strings.studiesDetected,
                locale,
                parse.rows.length,
                {
                  count: fmt(parse.rows.length),
                }
              ),
              scale:
                scale === "log"
                  ? strings.scaleWordLog
                  : strings.scaleWordLinear,
              none: fmt(scale === "log" ? 1 : 0),
              list: describe,
            })}
            headers={headers}
            id="fpm-chart"
            label={style.title.trim() || strings.chartLabel}
            layout={layout}
            title={style.apa ? "" : style.title}
          />
        </ChartFigure>
      ) : null}
      <ChartStyleFields
        axisLabels={{
          xAxisTitle: strings.xAxisTitle,
          axisTitlePlaceholderX: strings.axisTitlePlaceholderX,
        }}
        colorLabels={strings}
        idPrefix="fpm"
        labels={strings}
        style={style}
        update={update}
      />
    </div>
  );
}
